// ImageCropOverlay — client-side rectangle crop.
//
// Opens the box image fullscreen (same visual language as ImageTextSelector),
// lets the user drag a rectangle, then crops JUST that region to a PNG blob and
// hands it back via onCropped(blob). No backend — the crop is done with a
// canvas from a fetched blob (object-safe, never CORS-tainted).
//
// Adapted from ImageTextSelector.jsx: the crop math (fetch → blob →
// createImageBitmap → canvas.drawImage) and the viewport→natural coordinate
// mapping are the same; the OCR step is replaced with "return the cropped blob".

import { useState, useRef, useCallback, useEffect } from "react";
import i18n from "i18next";
import { credentialsFor } from "../../../common/utils";
import { LuX, LuLoaderCircle } from "react-icons/lu";

// Crop the given pixel rectangle (in NATURAL image coordinates) out of imageUrl
// and return it as a PNG Blob.
export async function cropRegion(imageUrl, rectNatural) {
  // Fetch → blob → bitmap keeps the crop same-origin (no canvas taint) even
  // when the image is served from a remote (Azure) origin without ACAO headers.
  const resp = await fetch(imageUrl, { mode: "cors", ...credentialsFor(imageUrl) });
  if (!resp.ok) throw new Error(`fetch ${resp.status}`);
  const blob = await resp.blob();
  const bitmap = await createImageBitmap(blob);

  const w = Math.max(1, Math.round(rectNatural.width));
  const h = Math.max(1, Math.round(rectNatural.height));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    bitmap,
    Math.round(rectNatural.left),
    Math.round(rectNatural.top),
    w,
    h,
    0,
    0,
    w,
    h
  );
  if (typeof bitmap.close === "function") bitmap.close();

  return await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob returned null"))), "image/png");
  });
}

export default function ImageCropOverlay({ imageUrl, onCropped, onClose }) {
  const imgRef = useRef(null);
  const [drag, setDrag] = useState(null); // { startX, startY, x, y } in viewport px
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Esc closes (unless mid-crop).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [loading, onClose]);

  const onMouseDown = useCallback(
    (e) => {
      if (loading) return;
      e.preventDefault();
      setError(null);
      setDrag({ startX: e.clientX, startY: e.clientY, x: e.clientX, y: e.clientY });
    },
    [loading]
  );

  const onMouseMove = useCallback(
    (e) => {
      if (!drag) return;
      setDrag((d) => (d ? { ...d, x: e.clientX, y: e.clientY } : d));
    },
    [drag]
  );

  const onMouseUp = useCallback(async () => {
    if (!drag) return;
    const sel = {
      left: Math.min(drag.startX, drag.x),
      top: Math.min(drag.startY, drag.y),
      width: Math.abs(drag.x - drag.startX),
      height: Math.abs(drag.y - drag.startY),
    };
    setDrag(null);
    if (sel.width < 5 || sel.height < 5) return; // accidental click

    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    // Intersect selection with the rendered image, then map to natural pixels.
    const left = Math.max(sel.left, rect.left);
    const top = Math.max(sel.top, rect.top);
    const right = Math.min(sel.left + sel.width, rect.right);
    const bottom = Math.min(sel.top + sel.height, rect.bottom);
    if (right <= left || bottom <= top) return;

    const scaleX = img.naturalWidth / rect.width;
    const scaleY = img.naturalHeight / rect.height;
    const rectNatural = {
      left: (left - rect.left) * scaleX,
      top: (top - rect.top) * scaleY,
      width: (right - left) * scaleX,
      height: (bottom - top) * scaleY,
    };

    setLoading(true);
    setError(null);
    try {
      const blob = await cropRegion(imageUrl, rectNatural);
      await onCropped?.(blob);
    } catch (err) {
      console.error("[ImageCropOverlay] Crop failed:", err);
      setError(i18n.t("CropFailed"));
      setLoading(false);
    }
  }, [drag, imageUrl, onCropped]);

  const selBox = drag
    ? {
        left: Math.min(drag.startX, drag.x),
        top: Math.min(drag.startY, drag.y),
        width: Math.abs(drag.x - drag.startX),
        height: Math.abs(drag.y - drag.startY),
      }
    : null;

  return (
    <div
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.9)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100000,
        cursor: loading ? "wait" : "crosshair",
        userSelect: "none",
      }}
    >
      <img
        ref={imgRef}
        src={imageUrl}
        alt="Crop"
        draggable={false}
        style={{
          maxWidth: "92vw",
          maxHeight: "88vh",
          objectFit: "contain",
          borderRadius: 8,
          pointerEvents: "none",
        }}
      />

      {/* Selection rectangle */}
      {selBox && selBox.width > 0 && selBox.height > 0 && (
        <div
          style={{
            position: "fixed",
            left: selBox.left,
            top: selBox.top,
            width: selBox.width,
            height: selBox.height,
            border: "2px solid #a963f5",
            background: "rgba(169,99,245,0.15)",
            pointerEvents: "none",
            zIndex: 100001,
          }}
        />
      )}

      {/* Instruction / status banner */}
      <div
        style={{
          position: "fixed",
          top: 18,
          left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(0,0,0,0.6)",
          color: "#fff",
          padding: "8px 16px",
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 8,
          backdropFilter: "blur(4px)",
          zIndex: 100002,
          pointerEvents: "none",
        }}
      >
        {loading ? (
          <>
            <LuLoaderCircle size={16} style={{ animation: "spin 1s linear infinite" }} />
            {i18n.t("Cropping")}
          </>
        ) : error ? (
          <span style={{ color: "#fca5a5" }}>{error}</span>
        ) : (
          i18n.t("DragToCrop")
        )}
      </div>

      {/* Close */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (!loading) onClose?.();
        }}
        title={i18n.t("Cancel")}
        style={{
          position: "fixed",
          top: 18,
          right: 18,
          background: "rgba(255,255,255,0.15)",
          border: "none",
          borderRadius: "50%",
          width: 38,
          height: 38,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          cursor: loading ? "not-allowed" : "pointer",
          backdropFilter: "blur(4px)",
          zIndex: 100002,
        }}
      >
        <LuX size={18} />
      </button>
    </div>
  );
}
